// app/utils/authcontext.tsx
'use client';

import { createContext, useContext, useState, ReactNode, useRef } from 'react';
import Cookies from 'js-cookie';
import * as Global from '@utils/global';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from "react";
import axios from "axios";

// ===============================
// 型定義
// ===============================
interface AuthContextProps {
  domainTitle: string | null;
  authToken: string | null;
  authTokenExpiresAt: string | null;
  employeeGUID: string | null;
  employeeDomain: string | null;
  employeeName: string | null;
  employeeFlag: string | null;
  setDomainTitle: (domain_title: string | null) => void;
  setAuthToken: (token: string | null) => void;
  setAuthTokenExpiresAt: (expires_at: string | null) => void;
  setEmployeeGUID: (user_guid: string | null) => void;
  setEmployeeDomain: (domain: string | null) => void;
  setEmployeeName: (user_name: string | null) => void;
  setEmployeeFlag: (user_inner_flag: string | null) => void;
  loaded: boolean;
}

// ===============================
// コンテキスト本体
// ===============================
const AuthContext = createContext<AuthContextProps | undefined>(undefined);
const isUnauthorizedError = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 401 || Global.isEmployeeNetworkDeniedError(error);

const rejectedSessionPath = (error: unknown): string =>
  Global.isEmployeeNetworkDeniedError(error) ? "/signon?reason=network_denied" : "/signon?reason=session_expired";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [domainTitle, setDomainTitle] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authTokenExpiresAt, setAuthTokenExpiresAt] = useState<string | null>(null);
  const [employeeGUID, setEmployeeGUID] = useState<string | null>(null);
  const [employeeDomain, setEmployeeDomain] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [employeeFlag, setEmployeeFlag] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(false);
  const sessionSyncingRef = useRef(false);
  const sessionRetryAtRef = useRef(0);
  const sessionCookieRef = useRef<{ token: string | null; domain: string | null }>({ token: null, domain: null });
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const token = Cookies.get(Global.COOKIE_AUTH_TOKEN) ?? null;
    if (!token) {
      setLoaded(true);
      return;
    }

    let cancelled = false;
    const restoreSession = async () => {
      try {
        // ブックマーク起動では、期限切れtokenの再発行と社員情報の復元を
        // トップメニュー取得より先に完了させる。社員DOMAIN Cookie欠落も復元可能。
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/personal/restore_session`, {
          token,
          ip_address: window.location.hostname,
          user_agent: navigator.userAgent,
        }, { timeout: 15000 });
        if (cancelled) return;
        Global.updateAuthTokenCookie(res.data.auth_token, res.data);
        const restoredToken = res.data.auth_token.token;
        sessionCookieRef.current = { token: restoredToken, domain: res.data.user_domain };
        setDomainTitle(res.data.domain_title);
        setAuthToken(restoredToken);
        setAuthTokenExpiresAt(res.data.auth_token.token_expires_at);
        setEmployeeGUID(res.data.user_guid);
        setEmployeeDomain(res.data.user_domain);
        setEmployeeName(res.data.user_name);
        setEmployeeFlag(String(res.data.user_inner_flag));
        sessionStorage.setItem("employeeVerified", "1");
        setLoaded(true);
      } catch (error) {
        if (cancelled) return;
        if (isUnauthorizedError(error)) {
          Global.clearAuthCookies();
          sessionStorage.removeItem("employeeVerified");
          setLoaded(true);
          router.replace(rejectedSessionPath(error));
          return;
        }
        // 一時的な通信障害では認証Cookieを消さず、再試行できる状態を保つ。
        setBootstrapError(true);
      }
    };
    restoreSession();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!loaded || pathname === "/signout") return;
    const syncSessionFromCookies = async () => {
      if (sessionSyncingRef.current || Date.now() < sessionRetryAtRef.current) return;
      const cookieToken = Cookies.get(Global.COOKIE_AUTH_TOKEN) ?? null;
      const cookieTokenExpiresAt = Cookies.get(Global.COOKIE_AUTH_TOKEN_EXPIRES_AT) ?? null;
      const cookieDomain = Cookies.get(Global.COOKIE_EMPLOYEE_DOMAIN) ?? null;
      const cookieDomainTitle = Cookies.get(Global.COOKIE_DOMAIN_TITLE) ?? null;
      const cookieName = Cookies.get(Global.COOKIE_EMPLOYEE_NAME) ?? null;
      const cookieFlag = Cookies.get(Global.COOKIE_EMPLOYEE_FLAG) ?? null;
      const domainChanged = cookieDomain !== sessionCookieRef.current.domain;
      const tokenChanged = cookieToken !== sessionCookieRef.current.token;
      const cookieChanged = cookieToken !== sessionCookieRef.current.token || cookieDomain !== sessionCookieRef.current.domain;
      if (!cookieChanged) return;
      if (!cookieToken) {
        const signoutInProgress = sessionStorage.getItem(Global.SESSION_SIGNOUT_IN_PROGRESS) === "1";
        sessionStorage.removeItem(Global.SESSION_SIGNOUT_IN_PROGRESS);
        sessionCookieRef.current = { token: null, domain: null };
        Global.clearAuthCookies();
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.replace(signoutInProgress ? "/signon" : "/signon?reason=session_expired");
        return;
      }
      sessionSyncingRef.current = true;
      try {
        const res = cookieDomain
          ? await axios.post<any>(`${Global.getApiBaseUrl()}/personal/me`, {
              base_token_interface: {
                token: cookieToken,
                user_domain: cookieDomain,
                ip_address: window.location.hostname,
                user_agent: navigator.userAgent,
              }
            })
          : await axios.post<any>(`${Global.getApiBaseUrl()}/personal/restore_session`, {
              token: cookieToken,
              ip_address: window.location.hostname,
              user_agent: navigator.userAgent,
            });
        if (res.data?.auth_token) {
          Global.updateAuthTokenCookie(res.data.auth_token, res.data);
        }
        const syncedToken = res.data?.auth_token?.token ?? cookieToken;
        const syncedDomain = res.data?.user_domain ?? cookieDomain;
        const userChanged = Boolean(employeeGUID && res.data?.user_guid && res.data.user_guid !== employeeGUID);
        sessionCookieRef.current = { token: syncedToken, domain: syncedDomain };
        if (tokenChanged || !cookieDomain) {
          setAuthToken(syncedToken);
          setAuthTokenExpiresAt(res.data?.auth_token?.token_expires_at ?? cookieTokenExpiresAt);
        }
        if (domainChanged || userChanged || pathname === "/signon") {
          setDomainTitle(res.data?.domain_title ?? cookieDomainTitle);
          setAuthToken(syncedToken);
          setAuthTokenExpiresAt(res.data?.auth_token?.token_expires_at ?? cookieTokenExpiresAt);
          setEmployeeGUID(res.data?.user_guid ?? null);
          setEmployeeDomain(syncedDomain);
          setEmployeeName(res.data?.user_name ?? cookieName);
          setEmployeeFlag(String(res.data?.user_inner_flag ?? cookieFlag ?? ""));
        }
        sessionStorage.setItem("employeeVerified", "1");
        if (domainChanged || userChanged || pathname === "/signon") {
          router.replace("/");
        }
      } catch (error) {
        if (!isUnauthorizedError(error)) {
          sessionRetryAtRef.current = Date.now() + 10000;
          return;
        }
        Global.clearAuthCookies();
        sessionCookieRef.current = { token: null, domain: null };
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.replace(rejectedSessionPath(error));
      } finally {
        sessionSyncingRef.current = false;
      }
    };
    const syncReissuedTokenFromCookie = () => {
      const cookieToken = Cookies.get(Global.COOKIE_AUTH_TOKEN) ?? null;
      const cookieDomain = Cookies.get(Global.COOKIE_EMPLOYEE_DOMAIN) ?? null;

      // API応答による同一タブ内のトークン再発行では、結果は既に取得済み。
      // Reactの認証状態は更新せず、次回の通常API呼び出しはCookieの新トークンを利用する。
      if (cookieToken && cookieDomain === sessionCookieRef.current.domain) {
        sessionCookieRef.current = { token: cookieToken, domain: cookieDomain };
      }
    };
    const intervalId = window.setInterval(syncSessionFromCookies, 1000);
    window.addEventListener(Global.SESSION_MISMATCH_EVENT, syncSessionFromCookies);
    window.addEventListener(Global.AUTH_TOKEN_UPDATED_EVENT, syncReissuedTokenFromCookie);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(Global.SESSION_MISMATCH_EVENT, syncSessionFromCookies);
      window.removeEventListener(Global.AUTH_TOKEN_UPDATED_EVENT, syncReissuedTokenFromCookie);
    };
  }, [loaded, pathname, router, employeeGUID]);

  // 🚨 ロード完了まで描画を止める（SSRの不整合防止）
  if (!loaded) {
    if (bootstrapError) {
      return (
        <div role="alert" style={{ padding: 24 }}>
          認証状態を確認できませんでした。通信状態を確認して再試行してください。
          <button type="button" onClick={() => window.location.reload()} style={{ marginLeft: 12 }}>
            再試行
          </button>
        </div>
      );
    }
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        domainTitle,
        setDomainTitle,
        authToken,
        setAuthToken,
        authTokenExpiresAt,
        setAuthTokenExpiresAt,
        employeeGUID,
        setEmployeeGUID,
        employeeDomain,
        setEmployeeDomain,
        employeeName,
        setEmployeeName,
        employeeFlag,
        setEmployeeFlag,
        loaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ===============================
// useAuth(): 認証情報アクセス
// ===============================
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return {
    ...ctx,
    // 社員判定
    isEmployee: ctx.employeeFlag ,
  };
};

// ===============================
// useAuthRedirect(): 認証リダイレクト
// ===============================
export const useAuthRedirect = () => {
  const { 
    domainTitle,
    authToken,
    employeeGUID,
    employeeDomain, 
    setDomainTitle, 
    setAuthToken,
    setAuthTokenExpiresAt,
    setEmployeeDomain, 
    setEmployeeName, 
    setEmployeeGUID, 
    setEmployeeFlag 
  } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const checkValidSession = async () => {
      // tokenなし → ログイン画面へ
      if (!authToken || !employeeDomain) {
        router.push("/signon");
        return;
      }

      // 🔒 一度検証済みならスキップ
      const verified = sessionStorage.getItem("employeeVerified");
      if (verified === "1" && employeeGUID) return;

      try {
        const currentToken = Global.getCurrentAuthToken(authToken);
        if (!currentToken) {
          throw new Error("認証トークンが取得できません");
        }
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/personal/me`, {
          base_token_interface: {
            token: currentToken,
            user_domain: employeeDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          }
        });

        if (res.data.user_name) {
          setDomainTitle(res.data.domain_title);
          setEmployeeGUID(res.data.user_guid);
          setEmployeeDomain(res.data.user_domain);
          setEmployeeName(res.data.user_name);
          setEmployeeFlag(res.data.user_inner_flag);
          sessionStorage.setItem("employeeVerified", "1");
        }
      } catch (e) {
        if (!isUnauthorizedError(e)) {
          // 通信障害やサーバーの一時的なエラーでは、再ログインを強制しない。
          return;
        }
        // 🔄 認証失敗 → ログアウト処理
        Cookies.remove(Global.COOKIE_DOMAIN_TITLE);
        Cookies.remove(Global.COOKIE_AUTH_TOKEN);
        Cookies.remove(Global.COOKIE_AUTH_TOKEN_EXPIRES_AT);
        Cookies.remove(Global.COOKIE_EMPLOYEE_DOMAIN);
        Cookies.remove(Global.COOKIE_EMPLOYEE_NAME);
        Cookies.remove(Global.COOKIE_EMPLOYEE_FLAG);
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.push(rejectedSessionPath(e));
      }
    };

    checkValidSession();
  }, [authToken, employeeDomain, employeeGUID, router]);
};
